// The Playbook: the home screen of the Sales tab, built like a game.
//
// The run card hands the rep the next store that is due, with the words
// and the outcome buttons; logging an outcome scores points, moves the row
// and pulls up the next store. Around it: today's goal ring and streak,
// the level, the scoreboard against the other reps, the last seven days,
// the badges, and the plan itself. All numbers come from salesGame.js,
// computed from the touch log; nothing extra is stored.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AtSign, BarChart3, CalendarCheck, Car, ChevronRight, Crown, DollarSign, ExternalLink, Flame, Footprints, Globe, Lightbulb, Lock, Mail, MapPin, Medal, Megaphone,
  Milestone, PenLine, Phone, Plus, Radio, RefreshCw, Rocket, Search, Shield, SkipForward, Sparkles, Sunrise, Swords, Target, Timer, Trophy, Tv, Upload, Users, Volume2, VolumeX, Zap,
} from 'lucide-react';
import { EXTRA_OUTCOME_LABEL, CADENCE, DEMO_STEPS, STAGE_ACTIONS, applyOutcome, areaOf, currentAction, dueBucket, fillScript, isCallback, isDoNotContact, isGoingCold, isOpenStage, stageOf, tempOf, withTemp } from './salesPlan';
import { claimProspect, e164, fetchProspect, insertTouch, loadEmails, prettyPhone, updateProspect } from './salesData';
import {
  BADGES, BASE_POINTS, CALLBACK_KEPT_POINTS, DAILY_GOAL, HOT_FOLLOWUP_POINTS, MIN_MONTHLY, NUDGE_HOUR, OUTCOME_POINTS, RUN_KINDS, SPRINT_METRIC_BY_ID, SPRINT_PRESETS, STAGE_BONUS, STREAK_MIN,
  buildQueue, countdown, money, pickSprint, repStats, scoreTouches, scoreboard, sprintStandings, sprintStatus, teamTally,
} from './salesGame';
import { chime, setSoundOn, soundOn } from './salesSound';
import { CallbackChip, DOT, Empty, REP_COLORS, RepChip, Sheet, TONE, StoreClock, TempChip, TempPicker, TierChip, btnGhost, btnPrimary, btnSmall, inputCls } from './salesUi';
import { FIRST_MONTH_RATE, PLAN_BY_KEY, RESIDUAL_RATE, money as dollars, repEarnings } from './salesCommission';
import ActionCard from './ActionCard';
import BriefCard from './BriefCard';
import ReplyDrafter from './ReplyDrafter';
import EmailComposer, { EmailThread, splitScript } from './EmailComposer';
import { assist } from './salesAssist';
import { MIN_TOUCHES, insights as computeInsights } from './salesInsights';

const ICONS = { phone: Phone, sunrise: Sunrise, flame: Flame, swords: Swords, medal: Medal, footprints: Footprints, car: Car, route: Milestone, tv: Tv, sparkles: Sparkles, rocket: Rocket, trophy: Trophy, megaphone: Megaphone, target: Target, zap: Zap, shield: Shield, pen: PenLine, search: Search, calendar: CalendarCheck };

const CSS = `
@keyframes salesPop{0%{opacity:0;transform:translateY(10px) scale(.9)}15%{opacity:1;transform:translateY(0) scale(1.06)}70%{opacity:1;transform:translateY(-16px) scale(1)}100%{opacity:0;transform:translateY(-40px) scale(.96)}}
@keyframes salesBump{0%{transform:scale(1)}35%{transform:scale(1.18)}100%{transform:scale(1)}}
@keyframes salesShine{0%{transform:translateX(-120%)}100%{transform:translateX(220%)}}
@keyframes salesLive{0%,100%{opacity:1}50%{opacity:.35}}
.sales-live{animation:salesLive 1.2s ease-in-out infinite}
.sales-pop{animation:salesPop 1.5s cubic-bezier(.2,.8,.2,1) forwards}
.sales-bump{animation:salesBump .45s ease-out}
.sales-shine::after{content:"";position:absolute;inset:0;width:40%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent);animation:salesShine 1.6s ease-in-out 1}
`;

const webUrl = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : '');
const socialUrl = (s) => (!s ? '' : /^https?:\/\//i.test(s) ? s : `https://www.instagram.com/${encodeURIComponent(s.replace(/^@/, ''))}`);
const mapsUrl = (p) => { const q = [p.name, p.address, p.city, p.state, p.zip].filter(Boolean).join(', '); return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : ''; };
const place = (p) => [p.city, p.state].filter(Boolean).join(', ');
const sinceText = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'less than an hour ago';
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

export default function PlaybookView({ prospects, touches, reps, repById, me, client, storeId, onChange, onTouch, onOpen, notify, onPickMe, onTeam, onImport, onAdd, dealsReady = false, briefReady = false, sprints = [], sprintsReady = false, onSprintStart = null, onSprintEnd = null, isAdmin = false }) {
  const now = new Date();
  const [sound, setSound] = useState(soundOn);
  const toggleSound = () => { const on = !sound; setSoundOn(on); setSound(on); if (on) chime('tick', { force: true }); };
  const scored = useMemo(() => scoreTouches(touches), [touches]);
  const stats = useMemo(() => (me ? repStats(scored, me.id, now) : null), [scored, me]); // eslint-disable-line react-hooks/exhaustive-deps
  const board = useMemo(() => scoreboard(scored, reps, 'week', now), [scored, reps]); // eslint-disable-line react-hooks/exhaustive-deps
  const rank = me ? board.findIndex((r) => r.rep.id === me.id) + 1 : 0;
  const earnings = useMemo(() => (me && dealsReady ? repEarnings(prospects, me.id, now) : null), [prospects, me, dealsReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const pipeline = useMemo(() => {
    const c = { pilot: 0, customer: 0, demo: 0 };
    for (const p of prospects) if (c[p.stage] != null) c[p.stage] += 1;
    return c;
  }, [prospects]);

  // Celebrations: level up and goal hit, only when they happen in this session.
  const [cheer, setCheer] = useState(null);
  const prev = useRef(null);
  useEffect(() => {
    if (!stats) { prev.current = null; return; }
    const was = prev.current;
    prev.current = { level: stats.level.level, goalHit: stats.goalHit, repId: stats.repId };
    if (!was || was.repId !== stats.repId) return;
    if (stats.level.level > was.level) setCheer({ icon: Trophy, text: `Level ${stats.level.level}: ${stats.level.title}` });
    else if (stats.goalHit && !was.goalHit) setCheer({ icon: Sparkles, text: `Daily goal hit. ${stats.streak.count > 1 ? `${stats.streak.count}-day streak.` : 'Streak started.'}` });
  }, [stats]);
  useEffect(() => {
    if (!cheer) return undefined;
    const t = setTimeout(() => setCheer(null), 5000);
    return () => clearTimeout(t);
  }, [cheer]);

  // Streak on the line: one nudge a day, in the afternoon, while the tab
  // is open. The hero shows it the whole time; this is the toast.
  useEffect(() => {
    if (!stats?.streak?.atRisk || new Date().getHours() < NUDGE_HOUR) return;
    const key = `sales.nudged.${stats.repId}`;
    const today = new Date().toDateString();
    try {
      if (window.localStorage.getItem(key) === today) return;
      window.localStorage.setItem(key, today);
    } catch { /* ignore */ }
    notify(`Your ${stats.streak.count}-day streak ends at midnight. ${Math.max(0, STREAK_MIN - stats.today.points)} more points keeps it.`, 'error');
  }, [stats, notify]);

  // Everything that is not the next call lives behind a chip: the main
  // screen is the hero and the run card, no scrolling. Opening a store from
  // a drawer closes the drawer first so the store drawer is on top.
  const [drawer, setDrawer] = useState(null);
  const openStore = useCallback((id) => { setDrawer(null); onOpen?.(id); }, [onOpen]);
  const DRAWERS = {
    board: { title: 'Scoreboard', subtitle: 'Points this week, and the last seven days.' },
    heat: { title: 'Hot board', subtitle: 'Every hot and warm store, who holds it, and the call-backs coming up.' },
    sprint: { title: 'Sprint', subtitle: 'A clock and a prize on one number.' },
    feed: { title: 'Team, live', subtitle: 'What everyone did in the last 48 hours, as it happens.' },
    badges: { title: 'Badges', subtitle: stats ? `${stats.badges.size} of ${BADGES.length} earned.` : '' },
    insights: { title: 'What our numbers say', subtitle: 'Which steps get answers, and when calls connect.' },
    plan: { title: 'The plan', subtitle: 'The seven touches, the points and the rules.' },
  };

  return (
    <div className="space-y-4">
      <style>{CSS}</style>
      <Hero me={me} stats={stats} rank={rank} repCount={reps.length} reps={reps} onPickMe={onPickMe} onTeam={onTeam} cheer={cheer} earnings={earnings} sound={sound} onToggleSound={toggleSound} />
      <Rail stats={stats} rank={rank} repCount={reps.length} sprints={sprints} scored={scored} reps={reps} me={me} isAdmin={isAdmin} onOpen={setDrawer} prospects={prospects} />
      <RunPanel prospects={prospects} scored={scored} me={me} reps={reps} repById={repById} client={client} storeId={storeId} onChange={onChange} onTouch={onTouch} onOpen={onOpen} notify={notify} onPickMe={onPickMe} onImport={onImport} onAdd={onAdd} stats={stats} dealsReady={dealsReady} briefReady={briefReady} />
      <Sheet open={!!drawer} onClose={() => setDrawer(null)} title={DRAWERS[drawer]?.title || ''} subtitle={DRAWERS[drawer]?.subtitle} width="lg">
        {drawer === 'board' && (
          <div className="space-y-4">
            <Scoreboard scored={scored} reps={reps} me={me} pipeline={pipeline} onTeam={onTeam} />
            {stats && <WeekBars stats={stats} />}
          </div>
        )}
        {drawer === 'sprint' && (
          <div className="space-y-4">
            <SprintCard sprints={sprints} ready={sprintsReady} scored={scored} reps={reps} me={me} isAdmin={isAdmin} onStart={onSprintStart} onEnd={onSprintEnd} />
            {!isAdmin && !pickSprint(sprints) && <p className="text-sm text-slate-500">No sprint running. Phillip starts them: most calls in the next hour, most walk-ins this week, that kind of thing, with a prize on it.</p>}
          </div>
        )}
        {drawer === 'feed' && (
          <div className="space-y-4">
            <TeamFeed scored={scored} prospects={prospects} repById={repById} me={me} onOpen={openStore} />
            {scored.length === 0 && <p className="text-sm text-slate-500">Nothing logged yet. The first call shows up here.</p>}
          </div>
        )}
        {drawer === 'heat' && <HotBoard prospects={prospects} reps={reps} repById={repById} me={me} onOpen={openStore} />}
        {drawer === 'badges' && stats && <BadgeGrid stats={stats} />}
        {drawer === 'insights' && <InsightsSection touches={touches} prospects={prospects} expanded />}
        {drawer === 'plan' && <PlanSection client={client} storeId={storeId} prospects={prospects} touches={touches} notify={notify} stacked />}
      </Sheet>
    </div>
  );
}

/* ---------------- the hot board ---------------- */

// Who is holding the heat. Hot stores first (going cold at the very top),
// then warm, then the call-backs promised for the next day. Read from the
// rows, nothing stored.
function HotBoard({ prospects, reps, repById, me, onOpen }) {
  const open = prospects.filter((p) => isOpenStage(p.stage) && !isDoNotContact(p));
  const hot = open.filter((p) => tempOf(p) === 'hot').sort((a, b) => Number(isGoingCold(b)) - Number(isGoingCold(a)) || new Date(a.last_touch_at || 0) - new Date(b.last_touch_at || 0));
  const warm = open.filter((p) => tempOf(p) === 'warm').sort((a, b) => new Date(a.next_due_at || 0) - new Date(b.next_due_at || 0));
  const soon = Date.now() + 86400000;
  const callbacks = open.filter((p) => isCallback(p) && new Date(p.next_due_at).getTime() <= soon).sort((a, b) => new Date(a.next_due_at) - new Date(b.next_due_at));
  const tally = reps.filter((r) => r.active !== false).map((r) => {
    const rows = open.filter((p) => p.rep_id === r.id);
    return { rep: r, hot: rows.filter((p) => tempOf(p) === 'hot').length, warm: rows.filter((p) => tempOf(p) === 'warm').length, cooling: rows.filter((p) => isGoingCold(p)).length };
  }).sort((a, b) => b.hot - a.hot || b.warm - a.warm);
  const Row = ({ p }) => (
    <li>
      <button type="button" onClick={() => onOpen(p.id)} className="w-full text-left py-2 flex items-center gap-2 hover:bg-slate-50 rounded-lg px-2 -mx-2">
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-slate-900 truncate">{p.name}</span>
          <span className="block text-xs text-slate-500 truncate">{[p.city, p.last_touch_at ? `last touch ${sinceText(p.last_touch_at)}` : 'not touched yet', isCallback(p) ? p.next_action : ''].filter(Boolean).join(' · ')}</span>
        </span>
        <TempChip prospect={p} small />
        {repById[p.rep_id] ? <RepChip rep={repById[p.rep_id]} /> : <span className="text-[11px] text-slate-400">pool</span>}
      </button>
    </li>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {tally.map((t) => (
          <div key={t.rep.id} className={`rounded-xl border p-3 ${t.rep.id === me?.id ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-center gap-2"><RepChip rep={t.rep} /><span className="font-semibold text-slate-900 truncate">{t.rep.name}</span></div>
            <div className="mt-1.5 text-sm text-slate-700"><span className="font-bold text-red-700">{t.hot} hot</span> · <span className="font-bold text-amber-700">{t.warm} warm</span></div>
            {t.cooling > 0 && <div className="text-xs font-semibold text-red-700 mt-0.5">{t.cooling} going cold. Touch them today.</div>}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">A hot store left alone for two days jumps back into its rep's queue. Touch a hot store within a day for +{HOT_FOLLOWUP_POINTS}; call back at the time you promised for +{CALLBACK_KEPT_POINTS}.</p>
      {callbacks.length > 0 && (
        <section>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-violet-700">Call-backs in the next day</h4>
          <ul className="mt-1 divide-y divide-slate-100">{callbacks.map((p) => <Row key={p.id} p={p} />)}</ul>
        </section>
      )}
      <section>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-red-700">Hot ({hot.length})</h4>
        {hot.length ? <ul className="mt-1 divide-y divide-slate-100">{hot.map((p) => <Row key={p.id} p={p} />)}</ul> : <p className="mt-1 text-sm text-slate-500">Nothing hot yet. A reply, a booked demo or a yes makes a store hot.</p>}
      </section>
      <section>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Warm ({warm.length})</h4>
        {warm.length ? <ul className="mt-1 divide-y divide-slate-100">{warm.slice(0, 40).map((p) => <Row key={p.id} p={p} />)}</ul> : <p className="mt-1 text-sm text-slate-500">A real conversation makes a store warm.</p>}
      </section>
    </div>
  );
}

/* ---------------- the rail: one chip per drawer ---------------- */

function Rail({ stats, rank, repCount, sprints, scored, reps, me, isAdmin, onOpen, prospects = [] }) {
  const [now, setNow] = useState(() => new Date());
  const sprint = useMemo(() => pickSprint(sprints, now), [sprints, now]);
  const st = sprint ? sprintStatus(sprint, now) : null;
  const ticking = st && st.status !== 'done';
  useEffect(() => {
    if (!ticking) return undefined;
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, [ticking]);
  const leader = sprint ? sprintStandings(scored, reps, sprint)[0] : null;
  const metric = sprint ? (SPRINT_METRIC_BY_ID[sprint.metric] || SPRINT_METRIC_BY_ID.calls) : null;
  const dayAgo = Date.now() - 86400000;
  const teamToday = scored.filter((t) => new Date(t.happened_at).getTime() >= dayAgo && t.kind !== 'stage' && t.kind !== 'assign' && (t.kind !== 'note' || t.outcome === 'brief')).length;
  const others = scored.filter((t) => new Date(t.happened_at).getTime() >= dayAgo && t.rep_id && t.rep_id !== me?.id && (t.outcome === 'reply_received' || (t.to_stage && t.to_stage !== t.from_stage && (t.to_stage === 'demo' || t.to_stage === 'pilot' || t.to_stage === 'customer')))).length;

  const sprintText = !sprint
    ? (isAdmin ? 'Start one' : 'None running')
    : st.status === 'live' ? `${countdown(st.msLeft)} left${leader && leader.value > 0 ? ` · ${leader.rep.name} ${leader.value}` : ''}`
      : st.status === 'upcoming' ? `starts in ${countdown(st.msLeft)}`
        : leader && leader.value > 0 ? `${leader.rep.name} won · ${leader.value} ${metric.label}` : 'Final';

  const mine = prospects.filter((p) => p.rep_id === me?.id && isOpenStage(p.stage));
  const myHot = mine.filter((p) => tempOf(p) === 'hot').length;
  const myCooling = mine.filter((p) => isGoingCold(p)).length;
  const chips = [
    { key: 'heat', icon: Flame, label: 'Hot board', value: myCooling ? `${myCooling} going cold` : myHot ? `${myHot} hot` : 'Nothing hot yet', tone: myCooling ? 'red' : myHot ? 'amber' : 'slate' },
    { key: 'board', icon: Crown, label: 'Scoreboard', value: stats && repCount > 1 && rank > 0 ? `#${rank} · ${stats.week.points} pts` : stats ? `${stats.week.points} pts this week` : 'Points', tone: rank === 1 && repCount > 1 ? 'amber' : 'slate' },
    { key: 'sprint', icon: sprint && st.status === 'live' ? Radio : Timer, label: sprint ? sprint.name : 'Sprint', value: sprintText, tone: sprint && st.status === 'live' ? 'red' : 'slate', live: !!(sprint && st.status === 'live') },
    { key: 'feed', icon: Users, label: 'Team, live', value: teamToday ? `${teamToday} today${others ? ` · ${others} big` : ''}` : 'Quiet today', tone: others ? 'emerald' : 'slate' },
    { key: 'badges', icon: Medal, label: 'Badges', value: stats ? `${stats.badges.size} of ${BADGES.length}` : `${BADGES.length}`, tone: 'slate' },
    { key: 'insights', icon: BarChart3, label: 'Numbers', value: 'When calls connect', tone: 'slate' },
    { key: 'plan', icon: Milestone, label: 'The plan', value: 'Seven touches, points, rules', tone: 'slate' },
  ];
  const TONES = {
    slate: 'border-slate-200 bg-white hover:border-slate-300',
    amber: 'border-amber-200 bg-amber-50 hover:border-amber-300',
    red: 'border-red-200 bg-red-50 hover:border-red-300',
    emerald: 'border-emerald-200 bg-emerald-50 hover:border-emerald-300',
  };
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-0.5">
      {chips.map((c) => (
        <button key={c.key} type="button" onClick={() => onOpen(c.key)} className={`flex-shrink-0 flex items-center gap-2.5 h-12 pl-3 pr-4 rounded-xl border text-left transition-colors ${TONES[c.tone]}`}>
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${c.tone === 'red' ? 'bg-red-600 text-white' : c.tone === 'amber' ? 'bg-amber-400 text-amber-950' : c.tone === 'emerald' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}><c.icon className={`w-4 h-4 ${c.live ? 'sales-live' : ''}`} /></span>
          <span className="min-w-0">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 leading-none">{c.label}</span>
            <span className={`block text-sm font-bold leading-tight mt-0.5 whitespace-nowrap ${c.tone === 'red' ? 'text-red-700' : 'text-slate-900'}`}>{c.value}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/* ---------------- hero ---------------- */

function Hero({ me, stats, rank, repCount, reps, onPickMe, onTeam, cheer, earnings, sound = false, onToggleSound = null }) {
  if (!me) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0"><Target className="w-6 h-6 text-blue-600" /></div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-slate-900">Who is playing?</h2>
            <p className="text-sm text-slate-500">Pick yourself so every call you log scores for you. The choice stays on this device.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {reps.map((r) => (
              <button key={r.id} type="button" onClick={() => onPickMe(r.id)} className="inline-flex items-center gap-2 h-11 pl-2 pr-4 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:shadow-sm text-sm font-semibold text-slate-800">
                <RepChip rep={r} size="md" /> {r.name}
              </button>
            ))}
            <button type="button" onClick={onTeam} className={btnGhost}><Users className="w-4 h-4" /> {reps.length ? 'Team' : 'Add the team'}</button>
          </div>
        </div>
      </div>
    );
  }
  const { level, today, week, streak } = stats;
  const pct = Math.min(1, today.points / DAILY_GOAL);
  const r = 26;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      {cheer && (
        <div className="absolute inset-x-0 top-0 z-10 flex justify-center pointer-events-none">
          <div className="sales-pop mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-amber-950 text-sm font-bold shadow-lg" style={{ animationDuration: '5s' }}>
            <cheer.icon className="w-4 h-4" /> {cheer.text}
          </div>
        </div>
      )}
      {onToggleSound && (
        <button type="button" onClick={onToggleSound} className="absolute top-3 right-3 w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center text-slate-500" title={sound ? 'Sounds on' : 'Sounds off'}>
          {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      )}
      {streak.atRisk && new Date().getHours() >= NUDGE_HOUR && (
        <div className="mb-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900 flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <span><span className="font-bold">Your {streak.count}-day streak ends at midnight.</span> {Math.max(0, STREAK_MIN - today.points)} more points keeps it alive. That is {Math.ceil(Math.max(0, STREAK_MIN - today.points) / (BASE_POINTS.call + OUTCOME_POINTS.spoke))} conversations or {Math.ceil(Math.max(0, STREAK_MIN - today.points) / BASE_POINTS.email)} sends.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4 sm:gap-6">
        {/* who + level */}
        <div className="flex items-center gap-3 min-w-0 basis-full sm:basis-auto sm:flex-1">
          <span className={`w-14 h-14 rounded-2xl ${REP_COLORS[me.color] || REP_COLORS.blue} text-white flex items-center justify-center text-xl font-bold flex-shrink-0 shadow-sm`}>{level.level}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-lg font-bold text-slate-900 truncate">{me.name}</h2>
              {rank > 0 && repCount > 1 && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${rank === 1 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {rank === 1 && <Crown className="w-3 h-3" />} #{rank} this week
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500">Level {level.level} · <span className="font-semibold text-slate-700">{level.title}</span></div>
            <div className="mt-1.5 w-56 max-w-full">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden relative">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-700" style={{ width: `${Math.round(level.progress * 100)}%` }} />
              </div>
              <div className="text-[11px] text-slate-400 mt-1">{level.nextXp ? `${level.xpToNext} points to ${level.nextTitle}` : 'Top level'} · {stats.all.points} all time</div>
            </div>
          </div>
        </div>

        {/* today ring */}
        <div className="flex items-center gap-3">
          <div className="relative w-16 h-16 flex-shrink-0">
            <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
              <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
              <circle cx="32" cy="32" r={r} fill="none" stroke={stats.goalHit ? '#059669' : '#2563eb'} strokeWidth="6" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} className="transition-all duration-700" />
            </svg>
            <div key={today.points} className="sales-bump absolute inset-0 flex items-center justify-center text-base font-bold text-slate-900">{today.points}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Today</div>
            <div className="text-sm font-bold text-slate-900">{stats.goalHit ? 'Goal hit' : `${DAILY_GOAL - today.points} to go`}</div>
            <div className="text-[11px] text-slate-400">{today.calls} calls · {today.sends} sends · {today.visits} visits</div>
          </div>
        </div>

        {/* streak */}
        <div className="flex items-center gap-3">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 ${streak.count > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50 border border-slate-200'}`}>
            <Flame className={`w-8 h-8 ${streak.count > 0 ? 'text-orange-500' : 'text-slate-300'}`} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Streak</div>
            <div className="text-sm font-bold text-slate-900">{streak.count} day{streak.count === 1 ? '' : 's'}</div>
            <div className="text-[11px] text-slate-400">{streak.atRisk ? `${Math.max(0, STREAK_MIN - today.points)} more today keeps it` : streak.count > 0 ? `Best ${stats.bestStreak}` : `${STREAK_MIN} points a day`}</div>
          </div>
        </div>

        {/* week */}
        <div className="hidden md:block">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">This week</div>
          <div className="text-2xl font-bold text-slate-900 leading-tight">{week.points}</div>
          <div className="text-[11px] text-slate-400">{week.demos} demos · {week.pilots} pilots · {week.customers} won</div>
        </div>

        {/* money */}
        {earnings && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">Earned</div>
            <div className="text-2xl font-bold text-emerald-800 leading-tight">{dollars(earnings.earned)}</div>
            <div className="text-[11px] text-emerald-700">{earnings.residual > 0 ? `${dollars(earnings.residual)} a month from ${earnings.active} store${earnings.active === 1 ? '' : 's'}` : 'First close pays the whole first month'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- the run ---------------- */

function RunPanel({ prospects, scored, me, reps, repById, client, storeId, onChange, onTouch, onOpen, notify, onPickMe, onImport, onAdd, stats, dealsReady, briefReady }) {
  const [replying, setReplying] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [kind, setKind] = useState('all');
  const [skipped, setSkipped] = useState(() => new Set());
  const [currentId, setCurrentId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [pops, setPops] = useState([]);
  const [run, setRun] = useState({ logged: 0, points: 0 });

  const queue = useMemo(() => buildQueue(prospects, { me, kind, skipped, actionOf: currentAction, bucketOf: dueBucket }), [prospects, me, kind, skipped]);
  const current = useMemo(() => queue.find((p) => p.id === currentId) || queue[0] || null, [queue, currentId]);
  const upNext = queue.filter((p) => p.id !== current?.id).slice(0, 3);
  const pool = useMemo(() => prospects.filter((p) => !p.rep_id && p.stage !== 'lost').length, [prospects]);
  const mineDue = useMemo(() => (me ? queue.filter((p) => p.rep_id === me.id).length : 0), [queue, me]);
  const tel = current ? e164(current.phone) : '';
  const action = current ? currentAction(current) : null;

  useEffect(() => { setNote(''); setReplying(false); setEmailing(false); }, [current?.id]);
  // The email thread on the current store, so a reply is visible right on the card.
  useEffect(() => {
    let alive = true;
    setEmails([]);
    if (!current?.id) return undefined;
    loadEmails(client, current.id).then(({ rows }) => { if (alive) setEmails(rows); }).catch((e) => console.error('sales: emails failed', e));
    return () => { alive = false; };
  }, [client, current?.id]);
  const emailScript = useMemo(() => (action?.kind === 'email' && current ? splitScript(fillScript(action.say, { prospect: current, rep: me })) : { subject: '', body: '' }), [action, current, me]);
  const emailOpen = emailing || action?.kind === 'email';
  const lastInbound = [...emails].reverse().find((m) => m.direction === 'in');

  // An unowned store is claimed the moment it comes up, so two reps can
  // never be dialing the same one. If someone else claimed it first the
  // update matches nothing; the row is refreshed and drops out of the queue.
  const claiming = useRef(new Set());
  useEffect(() => {
    if (!current || current.rep_id || !me || claiming.current.has(current.id)) return;
    claiming.current.add(current.id);
    const id = current.id;
    (async () => {
      try {
        const row = await claimProspect(client, id, me.id);
        if (row) { onChange(row); return; }
        const fresh = await fetchProspect(client, id);
        if (fresh) { onChange(fresh); notify(`${fresh.name} was just taken by ${repById[fresh.rep_id]?.name || 'another rep'}. Next one.`); }
        setCurrentId('');
      } catch (e) {
        console.error('sales: claim failed', e);
      } finally {
        claiming.current.delete(id);
      }
    })();
  }, [current?.id, current?.rep_id, me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pop = useCallback((text, big) => {
    const id = Math.random().toString(36).slice(2);
    setPops((prev) => [...prev, { id, text, big }]);
    setTimeout(() => setPops((prev) => prev.filter((x) => x.id !== id)), 1600);
  }, []);

  // Facts the tidy-up pulled from a note: fill empty fields, never overwrite.
  const applyFacts = async (f) => {
    if (!current) return;
    const patch = {};
    if (f.owner_name && !current.owner_name) patch.owner_name = String(f.owner_name).slice(0, 80);
    const extras = [f.decision_maker && `Decides: ${f.decision_maker}`, f.best_time && `Best time: ${f.best_time}`, f.competitor && `Uses: ${f.competitor}`].filter(Boolean);
    const cur = String(current.notes || '');
    const add = extras.filter((x) => !cur.includes(x));
    if (add.length) patch.notes = [cur.trim(), ...add].filter(Boolean).join('\n');
    if (!Object.keys(patch).length) return;
    try { onChange(await updateProspect(client, current.id, patch)); } catch (e) { console.error('sales: facts failed', e); }
  };

  const briefSaved = async (patch) => {
    if (!current) return null;
    try { const row = await updateProspect(client, current.id, patch); onChange(row); return row; } catch (e) { console.error('sales: brief save failed', e); return null; }
  };
  const briefTouch = async () => {
    if (!current) return;
    try {
      const t = await insertTouch(client, { store_id: storeId, prospect_id: current.id, kind: 'note', outcome: 'brief', note: 'Built the brief', from_stage: current.stage, to_stage: current.stage, rep_id: me?.id || null, rep_name: me?.name || null, happened_at: new Date().toISOString() });
      onTouch(t);
    } catch (e) { console.error('sales: brief touch failed', e); }
  };

  const onOutcome = async (o, text, extra, opts = {}) => {
    if (!current || busy) return null;
    setBusy(true);
    try {
      const { patch, touch } = applyOutcome(current, o, { note: text, rep: me, when: opts.when || null, never: !!opts.never });
      if (!current.rep_id && me) patch.rep_id = me.id;
      if (extra) Object.assign(patch, extra);
      const row = await updateProspect(client, current.id, patch);
      onChange(row);
      const t = await insertTouch(client, { ...touch, store_id: storeId, prospect_id: current.id });
      onTouch(t);
      const mine = scoreTouches([...scored, t]).find((x) => x.id === t.id);
      const pts = mine?.points || 0;
      const moved = row.stage !== current.stage;
      pop(pts > 0 ? `+${pts}` : 'Logged', mine?.bonusStage ? `${stageOf(mine.bonusStage).label}!` : moved ? stageOf(row.stage).label : o.label);
      if (mine?.bonusStage) chime('win'); else if (pts > 0) chime('tick');
      setRun((r) => ({ logged: r.logged + 1, points: r.points + pts }));
      if (moved && (row.stage === 'demo' || row.stage === 'pilot' || row.stage === 'customer')) notify(`${current.name} moved to ${stageOf(row.stage).label}. +${pts}`);
      setNote('');
      setCurrentId('');
      return row;
    } catch (e) {
      console.error('sales: run log failed', e);
      notify('Could not save that. Try again.', 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  // Hot / warm / cold by hand. Not a touch: no points, nothing in the log.
  const setTemp = async (temp) => {
    if (!current) return;
    try { const row = await updateProspect(client, current.id, { tags: withTemp(current.tags, temp), updated_at: new Date().toISOString() }); onChange(row); } catch (e) { console.error('sales: temp failed', e); notify('Could not save that. Try again.', 'error'); }
  };

  const skip = () => {
    if (!current) return;
    setSkipped((prev) => new Set(prev).add(current.id));
    setCurrentId('');
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0"><Zap className="w-4 h-4" /></span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-slate-900 leading-tight">Next up</h3>
            <div className="text-xs text-slate-500">{queue.length === 0 ? 'Queue clear' : `${mineDue} of yours due`}{pool > 0 && <span> · {pool.toLocaleString()} in the pool</span>}{run.logged > 0 && <span className="text-slate-400"> · this run {run.logged} logged, +{run.points}</span>}</div>
          </div>
        </div>
        <div className="flex bg-slate-100 rounded-lg p-1 overflow-x-auto no-scrollbar max-w-full ml-auto">
          {RUN_KINDS.map((k) => (
            <button key={k.id} type="button" onClick={() => setKind(k.id)} className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${kind === k.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{k.label}</button>
          ))}
        </div>
      </div>

      <div className="relative p-4 sm:p-5">
        {/* point pops */}
        <div className="pointer-events-none absolute right-5 top-3 z-10 flex flex-col items-end">
          {pops.map((x) => (
            <div key={x.id} className="sales-pop flex items-baseline gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white shadow-lg">
              <span className="text-lg font-bold leading-none">{x.text}</span>
              {x.big && <span className="text-xs font-semibold opacity-90">{x.big}</span>}
            </div>
          ))}
        </div>

        {!me ? (
          <Empty icon={Users} title="Pick who you are to start" body="Your stores and the unclaimed ones line up here, due first." action={<div className="flex flex-wrap gap-2 justify-center">{reps.map((r) => <button key={r.id} type="button" onClick={() => onPickMe(r.id)} className={btnGhost}><RepChip rep={r} /> {r.name}</button>)}</div>} />
        ) : !current ? (
          <QueueClear stats={stats} kind={kind} total={prospects.length} onKind={() => setKind('all')} onImport={onImport} onAdd={onAdd} skippedCount={skipped.size} onUnskip={() => setSkipped(new Set())} />
        ) : (
          <>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <button type="button" onClick={() => onOpen(current.id)} className="text-xl font-bold text-slate-900 truncate hover:text-blue-700 text-left" title="Open the store">{current.name}</button>
                  {!current.rep_id ? <span className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-[11px] font-bold text-amber-800">Claiming for you</span> : current.rep_id === me?.id && (current.touch_count || 0) === 0 ? <span className="px-2 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-[11px] font-bold text-blue-800">Yours. First touch</span> : null}
                  {areaOf(current) && <span className="px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600">{areaOf(current)}</span>}
                  <TierChip prospect={current} />
                  <CallbackChip prospect={current} />
                  <StoreClock prospect={current} />
                </div>
                <div className="mt-1.5"><TempPicker prospect={current} disabled={busy} onPick={setTemp} /></div>
                <div className="text-sm text-slate-500 mt-0.5 truncate">{[current.owner_name && `Owner: ${current.owner_name}`, place(current), current.address].filter(Boolean).join(' · ') || 'No owner or address on file'}</div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {tel ? (
                    <a href={`tel:${tel}`} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold"><Phone className="w-4 h-4" /> {prettyPhone(current.phone)}</a>
                  ) : <span className={btnSmall}>No phone on file</span>}
                  {current.email && <span className={`${btnSmall} cursor-default`} title="Email on file">{current.email}</span>}
                  {current.website && <a href={webUrl(current.website)} target="_blank" rel="noopener noreferrer" className={btnSmall}><Globe className="w-3.5 h-3.5" /> Site</a>}
                  {current.social && <a href={socialUrl(current.social)} target="_blank" rel="noopener noreferrer" className={btnSmall}><AtSign className="w-3.5 h-3.5" /> Social</a>}
                  {mapsUrl(current) && <a href={mapsUrl(current)} target="_blank" rel="noopener noreferrer" className={btnSmall}><MapPin className="w-3.5 h-3.5" /> Map</a>}
                </div>
                {current.last_touch_at && <p className="mt-2 text-xs text-slate-500">Last touch {sinceText(current.last_touch_at)}{current.touch_count ? `, ${current.touch_count} so far` : ''}. Only you work this store.</p>}
                {current.notes && <p className="mt-1 text-xs text-slate-500 line-clamp-2">{current.notes}</p>}
              </div>
              <div className="flex items-center gap-2 ml-auto -mt-1 sm:mt-0">
                <button type="button" onClick={() => onOpen(current.id)} className={btnSmall} title="Open the full store: edit, notes, log"><ExternalLink className="w-3.5 h-3.5" /> Open</button>
                <button type="button" onClick={skip} className={btnSmall} title="Not now. Moves it to the back of the queue"><SkipForward className="w-3.5 h-3.5" /> Skip</button>
              </div>
            </div>

            <div className="mt-3">
              <BriefCard client={client} storeId={storeId} prospect={current} me={me} briefReady={briefReady} notify={notify} onSaved={briefSaved} onBuilt={briefTouch} compact />
            </div>

            {emails.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email thread</div>
                <EmailThread emails={emails.slice(-3)} onReply={() => setReplying(true)} />
              </div>
            )}

            {emailOpen && current.email !== undefined && (
              <div className="mt-3">
                <EmailComposer
                  key={lastInbound?.id || 'new'}
                  client={client}
                  storeId={storeId}
                  prospect={current}
                  me={me}
                  initialSubject={emailScript.subject || (lastInbound?.subject ? (lastInbound.subject.startsWith('Re:') ? lastInbound.subject : `Re: ${lastInbound.subject}`) : '')}
                  initialBody={emailScript.body}
                  context={lastInbound?.body_text || ''}
                  notify={notify}
                  compact
                  onClose={action?.kind === 'email' ? undefined : () => setEmailing(false)}
                  onSent={async ({ subject, row, external }) => {
                    if (row) setEmails((prev) => [...prev, row]);
                    // Sending from the app IS the step when the step is an email: log it and move on.
                    if (action?.kind === 'email' && !external) {
                      const sent = action.outcomes.find((o) => o.key === (action.isReply ? 'answered' : 'sent'));
                      if (sent) await onOutcome(sent, action.isReply ? 'Answered in the app' : 'Sent from the app');
                    }
                    setEmailing(false);
                  }}
                />
              </div>
            )}

            <WorthLine prospect={current} />
            <div className="mt-3">
              <ActionCard prospect={current} me={me} busy={busy} onOutcome={onOutcome} note={note} setNote={setNote} hotkeys dealsReady={dealsReady} reps={reps} client={client} storeId={storeId} onFacts={applyFacts} hideScript={emailOpen && action?.kind === 'email'} inbound={lastInbound} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              {!replying && <button type="button" onClick={() => setReplying(true)} className="text-xs font-semibold text-violet-700 hover:underline">They wrote back? Draft a reply</button>}
              {!emailOpen && current.email && <button type="button" onClick={() => setEmailing(true)} className="text-xs font-semibold text-sky-700 hover:underline">Email them from here</button>}
            </div>
            {replying && (
              <div className="mt-2">
                <ReplyDrafter client={client} storeId={storeId} prospect={current} me={me} initialText={lastInbound?.body_text || ''} onClose={() => setReplying(false)} onUseAsNote={(t) => { setNote(t); setReplying(false); }} />
              </div>
            )}

            {upNext.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Then</div>
                <div className="flex flex-wrap gap-2">
                  {upNext.map((p) => {
                    const a = currentAction(p);
                    return (
                      <button key={p.id} type="button" onClick={() => setCurrentId(p.id)} className="inline-flex items-center gap-2 max-w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300 text-left">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[stageOf(p.stage).tone]}`} />
                        <span className="text-sm font-semibold text-slate-800 truncate">{p.name}</span>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{a?.short || a?.label}</span>
                        <RepChip rep={repById[p.rep_id]} />
                      </button>
                    );
                  })}
                  {queue.length > 4 && <span className="inline-flex items-center px-2 text-xs text-slate-400">+{queue.length - 4} more</span>}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QueueClear({ stats, kind, total, onKind, onImport, onAdd, skippedCount, onUnskip }) {
  if (total === 0) {
    return <Empty icon={Upload} title="No stores on the list yet" body={onImport ? 'Import the store list and the first calls line up here.' : 'Phillip loads the store list. Once it is in, your first calls line up here.'} action={<div className="flex gap-2">{onImport && <button type="button" onClick={onImport} className={btnPrimary}><Upload className="w-4 h-4" /> Import list</button>}<button type="button" onClick={onAdd} className={btnGhost}><Plus className="w-4 h-4" /> Add store</button></div>} />;
  }
  const t = stats?.today;
  return (
    <div className="relative overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center sales-shine">
      <div className="w-12 h-12 rounded-xl bg-white border border-emerald-200 flex items-center justify-center mx-auto"><Trophy className="w-6 h-6 text-emerald-600" /></div>
      <h3 className="text-lg font-bold text-slate-900 mt-3">{kind === 'all' ? 'Queue clear' : `No ${RUN_KINDS.find((k) => k.id === kind)?.label.toLowerCase()} due`}</h3>
      {t && <p className="text-sm text-slate-600 mt-1">Today: {t.calls} calls, {t.sends} sends, {t.visits} visits, {t.demos} demos. {t.points} points.</p>}
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {kind !== 'all' && <button type="button" onClick={onKind} className={btnGhost}>Show everything due</button>}
        {skippedCount > 0 && <button type="button" onClick={onUnskip} className={btnGhost}>Bring back {skippedCount} skipped</button>}
        <button type="button" onClick={onAdd} className={btnGhost}><Plus className="w-4 h-4" /> Add a store</button>
        {onImport && <button type="button" onClick={onImport} className={btnGhost}><Upload className="w-4 h-4" /> Import more</button>}
      </div>
    </div>
  );
}

/* ---------------- scoreboard ---------------- */

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'all', label: 'All time' },
];

function Scoreboard({ scored, reps, me, pipeline, onTeam }) {
  const [period, setPeriod] = useState('week');
  const rows = useMemo(() => scoreboard(scored, reps, period), [scored, reps, period]);
  const team = useMemo(() => teamTally(scored, period), [scored, period]);
  const top = rows[0]?.points || 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <Crown className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-bold text-slate-900">Scoreboard</h3>
        <div className="ml-auto flex bg-slate-100 rounded-lg p-0.5">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" onClick={() => setPeriod(p.id)} className={`px-2 py-1 rounded-md text-[11px] font-semibold ${period === p.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>{p.label}</button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500">No team yet. <button type="button" onClick={onTeam} className="font-semibold text-blue-700 underline">Add the reps</button>.</div>
      ) : (
        <ol className="mt-3 space-y-2">
          {rows.map((r, i) => {
            const lead = i === 0 && r.points > 0;
            const isMe = me?.id === r.rep.id;
            return (
              <li key={r.rep.id} className={`rounded-xl border p-3 ${lead ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200 bg-white'} ${isMe ? 'ring-2 ring-blue-500/15' : ''}`}>
                <div className="flex items-center gap-3">
                  <span className="w-5 text-center text-sm font-bold text-slate-400">{lead ? <Crown className="w-4 h-4 text-amber-500 inline" /> : i + 1}</span>
                  <RepChip rep={r.rep} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-slate-900 truncate">{r.rep.name}{isMe && <span className="text-slate-400 font-medium"> (you)</span>}</div>
                    <div className="text-[11px] text-slate-500">{r.calls} calls · {r.sends} sends · {r.visits} visits · {r.demos} demos · {r.pilots} pilots</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-slate-900 leading-none">{r.points}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">pts</div>
                  </div>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${REP_COLORS[r.rep.color] || REP_COLORS.blue} transition-all duration-700`} style={{ width: `${top ? Math.max(3, Math.round((r.points / top) * 100)) : 0}%` }} />
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
        <div><span className="font-semibold text-slate-700">Team {PERIODS.find((p) => p.id === period)?.label.toLowerCase()}:</span> {team.calls} calls, {team.visits} visits, {team.demos} demos, {team.pilots} pilots.</div>
        <div><span className="font-semibold text-slate-700">On the board:</span> {pipeline.demo} demos, {pipeline.pilot} pilots, {pipeline.customer} customers{pipeline.customer > 0 && <> · at least <span className="font-bold text-emerald-700">{money(pipeline.customer * MIN_MONTHLY)}</span> a month</>}.</div>
      </div>
    </div>
  );
}

/* ---------------- what a store is worth to the rep ---------------- */

// The rule from salesCommission: the whole first month, then 10% of every
// month after. Shown on the run card so the money is in front of the rep
// on every call, not on a tab. A deal already on the row uses its amount;
// otherwise the plan most stores take.
function WorthLine({ prospect: p }) {
  if (!p || p.stage === 'lost' || p.stage === 'customer') return null;
  const monthly = Number(p.monthly_amount) > 0 ? Number(p.monthly_amount) : PLAN_BY_KEY.selling.amount;
  const first = monthly * FIRST_MONTH_RATE;
  const residual = monthly * RESIDUAL_RATE;
  const label = Number(p.monthly_amount) > 0 ? 'their deal' : PLAN_BY_KEY.selling.label;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold"><DollarSign className="w-3.5 h-3.5" /> {dollars(first)} to you at close</span>
      <span className="text-slate-600">then <span className="font-semibold text-slate-800">{dollars(residual)} a month</span> for as long as they stay{p.stage === 'pilot' ? '. They are on the pilot: ask for the conversion.' : ` (${label}).`}</span>
    </div>
  );
}

/* ---------------- sprint ---------------- */

// A short contest on one number, with a clock on it. Phillip starts one
// from a preset; standings come from the touches in the window and move
// as they land. Ends by itself; the result stays on the board for a day.
function SprintCard({ sprints, ready, scored, reps, me, isAdmin, onStart, onEnd }) {
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);
  const [prize, setPrize] = useState('');
  const [busy, setBusy] = useState(false);
  const sprint = useMemo(() => pickSprint(sprints, now), [sprints, now]);
  const live = sprint ? sprintStatus(sprint, now) : null;
  const ticking = live && live.status !== 'done';
  useEffect(() => {
    if (!ticking) return undefined;
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, [ticking]);
  // Hearing the sprint end while watching, once.
  const wasLive = useRef(false);
  useEffect(() => {
    if (!sprint) { wasLive.current = false; return; }
    if (live.status === 'live') wasLive.current = true;
    else if (live.status === 'done' && wasLive.current) { wasLive.current = false; chime('bell'); setNow(new Date()); }
  }, [sprint, live?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (preset) => {
    if (!onStart || busy) return;
    setBusy(true);
    try { const row = await onStart(preset, prize); if (row) { setOpen(false); setPrize(''); } } finally { setBusy(false); }
  };

  const starter = (
    <div className="mt-3">
      {!ready && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Sprints are not set up yet. Run <code>sql/sales-pipeline/07_live_and_sprints.sql</code> once.</p>}
      {ready && !open && <button type="button" onClick={() => setOpen(true)} className={btnPrimary}><Timer className="w-4 h-4" /> Start a sprint</button>}
      {ready && open && (
        <div>
          <input value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="Prize (optional): $50, lunch, pick the music" className={inputCls} />
          <div className="mt-2 grid grid-cols-1 gap-1.5">
            {SPRINT_PRESETS.map((pr) => (
              <button key={pr.key} type="button" disabled={busy} onClick={() => start(pr)} className="text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50">
                <div className="text-sm font-bold text-slate-900">{pr.name}</div>
                <div className="text-[11px] text-slate-500">{pr.hint}</div>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setOpen(false)} className={`${btnGhost} mt-2`}>Cancel</button>
        </div>
      )}
    </div>
  );

  if (!sprint) {
    if (!isAdmin) return null;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2"><Timer className="w-4 h-4 text-slate-500" /><h3 className="text-sm font-bold text-slate-900">Sprint</h3></div>
        <p className="mt-1 text-xs text-slate-500">A clock and a prize on one number. Nothing running.</p>
        {starter}
      </div>
    );
  }

  const metric = SPRINT_METRIC_BY_ID[sprint.metric] || SPRINT_METRIC_BY_ID.calls;
  const rows = sprintStandings(scored, reps, sprint);
  const top = rows[0]?.value || 0;
  const leaders = rows.filter((r) => r.value === top && top > 0);
  const done = live.status === 'done';
  const upcoming = live.status === 'upcoming';
  const frame = done ? 'border-slate-200 bg-white' : 'border-red-200 bg-red-50/40';
  return (
    <div className={`rounded-2xl border p-4 ${frame}`}>
      <div className="flex items-center gap-2">
        {done ? <Trophy className="w-4 h-4 text-amber-500" /> : <Radio className={`w-4 h-4 text-red-600 ${upcoming ? '' : 'sales-live'}`} />}
        <h3 className="text-sm font-bold text-slate-900 truncate">{sprint.name}</h3>
        <span className={`ml-auto px-2 py-0.5 rounded-full text-[11px] font-bold border ${done ? 'bg-slate-50 text-slate-600 border-slate-200' : upcoming ? 'bg-white text-slate-700 border-slate-200' : 'bg-red-600 text-white border-red-600'}`}>{done ? 'Final' : upcoming ? 'Starts soon' : 'LIVE'}</span>
      </div>
      <div className="mt-2 flex items-end gap-3">
        <div className={`text-3xl font-bold tabular-nums leading-none ${done ? 'text-slate-400' : 'text-slate-900'}`}>{done ? '0:00' : countdown(live.msLeft)}</div>
        <div className="text-xs text-slate-500 pb-0.5">{done ? `Most ${metric.label}. Ended ${new Date(sprint.ends_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}.` : upcoming ? `until it starts · most ${metric.label}` : `left · most ${metric.label}`}</div>
      </div>
      {sprint.prize && <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1"><Trophy className="w-3 h-3" /> {sprint.prize}</div>}
      {done && (
        <div className="mt-2 text-sm font-bold text-slate-900">
          {leaders.length === 0 ? 'Nobody scored.' : leaders.length > 1 ? `Tie: ${leaders.map((r) => r.rep.name).join(' and ')} with ${top}.` : `${leaders[0].rep.name} wins with ${top} ${top === 1 ? metric.noun : metric.label}.${me?.id === leaders[0].rep.id ? ' That is you.' : ''}`}
        </div>
      )}
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => {
          const isMe = me?.id === r.rep.id;
          const lead = i === 0 && r.value > 0;
          return (
            <li key={r.rep.id} className="flex items-center gap-2">
              <span className="w-4 text-center text-xs font-bold text-slate-400">{lead ? <Crown className="w-3.5 h-3.5 text-amber-500 inline" /> : i + 1}</span>
              <RepChip rep={r.rep} size="sm" />
              <span className={`text-sm truncate ${isMe ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{r.rep.name}</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${REP_COLORS[r.rep.color] || REP_COLORS.blue} transition-all duration-700`} style={{ width: `${top ? Math.max(3, Math.round((r.value / top) * 100)) : 0}%` }} /></div>
              <span key={r.value} className="sales-bump text-sm font-bold tabular-nums text-slate-900 w-7 text-right">{r.value}</span>
            </li>
          );
        })}
      </ol>
      {isAdmin && !done && onEnd && <button type="button" onClick={() => onEnd(sprint.id)} className={`${btnSmall} mt-3`}>End it now</button>}
      {isAdmin && done && starter}
    </div>
  );
}

/* ---------------- team feed ---------------- */

const outcomeLabel = (t) => {
  const steps = [...CADENCE, ...DEMO_STEPS, ...Object.values(STAGE_ACTIONS).filter(Boolean)];
  for (const s of steps) {
    const o = (s.outcomes || []).find((x) => x.key === t.outcome);
    if (o) return o.label;
  }
  return EXTRA_OUTCOME_LABEL[t.outcome] || (t.outcome ? t.outcome.replace(/_/g, ' ') : t.kind);
};

// What everyone did, newest first, as it happens. The big ones are
// highlighted. Tap a row to open the store.
function TeamFeed({ scored, prospects, repById, me, onOpen }) {
  const byId = useMemo(() => Object.fromEntries(prospects.map((p) => [p.id, p])), [prospects]);
  const rows = useMemo(() => {
    const since = Date.now() - 48 * 3600000;
    return [...scored]
      .filter((t) => new Date(t.happened_at).getTime() >= since && t.kind !== 'stage' && t.kind !== 'assign' && (t.kind !== 'note' || t.outcome === 'brief'))
      .sort((a, b) => new Date(b.happened_at) - new Date(a.happened_at))
      .slice(0, 12);
  }, [scored]);
  if (rows.length === 0) return null;
  const when = (iso) => {
    const d = new Date(iso);
    const today = d.toDateString() === new Date().toDateString();
    return today ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2"><Radio className="w-4 h-4 text-slate-500" /><h3 className="text-sm font-bold text-slate-900">Team, live</h3><span className="ml-auto text-[11px] text-slate-400">last 48 hours</span></div>
      <ol className="mt-3 space-y-1.5">
        {rows.map((t) => {
          const p = byId[t.prospect_id];
          const rep = t.rep_id ? repById[t.rep_id] : null;
          const moved = t.to_stage && t.to_stage !== t.from_stage;
          const reply = t.outcome === 'reply_received' || t.outcome === 'demo_line';
          const big = reply || (moved && (t.to_stage === 'demo' || t.to_stage === 'pilot' || t.to_stage === 'customer'));
          const Icon = t.outcome === 'demo_line' ? Phone : reply ? Mail : KIND_ICON[t.kind] || Zap;
          const text = t.outcome === 'demo_line'
            ? `${p?.name || 'A store'} called the demo line`
            : reply
            ? `${p?.name || 'A store'} wrote back`
            : moved && t.to_stage === 'customer' ? `closed ${p?.name || 'a store'}`
              : moved && t.to_stage === 'pilot' ? `signed a pilot at ${p?.name || 'a store'}`
                : moved && t.to_stage === 'demo' ? `booked a demo at ${p?.name || 'a store'}`
                  : `${outcomeLabel(t)} · ${p?.name || 'a store'}`;
          return (
            <li key={t.id}>
              <button type="button" onClick={() => p && onOpen?.(p.id)} className={`w-full text-left flex items-center gap-2 rounded-lg px-2 py-1.5 ${big ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50'}`}>
                {rep ? <RepChip rep={rep} size="sm" /> : <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0"><Icon className="w-3 h-3 text-slate-500" /></span>}
                <span className="min-w-0 flex-1 text-sm text-slate-800 truncate">{!reply && <span className={`font-semibold ${me?.id === t.rep_id ? 'text-blue-700' : 'text-slate-900'}`}>{t.rep_name || rep?.name || 'Someone'}</span>}{!reply && ' '}{text}</span>
                {t.points > 0 && <span className="text-[11px] font-bold text-slate-500">+{t.points}</span>}
                <span className="text-[11px] text-slate-400 tabular-nums flex-shrink-0">{when(t.happened_at)}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ---------------- last 7 days ---------------- */

function WeekBars({ stats }) {
  const max = Math.max(DAILY_GOAL, ...stats.last7.map((d) => d.points));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">Last 7 days</h3>
        <span className="text-[11px] text-slate-400">goal {DAILY_GOAL} a day</span>
      </div>
      <div className="mt-3 relative h-24">
        <div className="absolute left-0 right-0 border-t border-dashed border-slate-300" style={{ bottom: `${(DAILY_GOAL / max) * 100}%` }} />
        <div className="absolute inset-0 flex items-end gap-1.5">
          {stats.last7.map((d) => (
            <div key={d.key} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.points} points`}>
              <div className={`w-full rounded-t-md transition-all duration-700 ${d.points >= DAILY_GOAL ? 'bg-emerald-500' : d.isToday ? 'bg-blue-500' : 'bg-slate-300'}`} style={{ height: `${Math.max(d.points > 0 ? 4 : 2, (d.points / max) * 100)}%` }} />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-1 flex gap-1.5">
        {stats.last7.map((d) => <div key={d.key} className={`flex-1 text-center text-[11px] ${d.isToday ? 'font-bold text-slate-900' : 'text-slate-400'}`}>{d.label}</div>)}
      </div>
    </div>
  );
}

/* ---------------- badges ---------------- */

function BadgeGrid({ stats }) {
  const got = stats.badges;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">Badges</h3>
        <span className="text-[11px] text-slate-400">{got.size} of {BADGES.length}</span>
      </div>
      <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-4 gap-2">
        {BADGES.map((b) => {
          const Icon = ICONS[b.icon] || Medal;
          const on = got.has(b.key);
          return (
            <div key={b.key} className={`group relative aspect-square rounded-xl border flex flex-col items-center justify-center gap-1 p-1 text-center ${on ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50' : 'border-slate-200 bg-slate-50'}`} title={`${b.name}: ${b.hint}`}>
              {on ? <Icon className="w-5 h-5 text-amber-600" /> : <Lock className="w-4 h-4 text-slate-300" />}
              <span className={`text-[10px] font-semibold leading-tight ${on ? 'text-amber-900' : 'text-slate-400'}`}>{b.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- what the numbers say ---------------- */

const Bar = ({ pct, tone = 'bg-blue-500' }) => (
  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} /></div>
);

function InsightsSection({ touches, prospects, expanded = false }) {
  const ins = useMemo(() => computeInsights(touches, prospects), [touches, prospects]);
  const [open, setOpen] = useState(!!expanded);
  const bestHour = ins.byHour.filter((h) => h.calls >= 5).sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
  const bestDay = ins.byWeekday.filter((d) => d.calls >= 5).sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
  const fmtHour = (h) => new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full px-4 sm:px-5 py-3 flex flex-wrap items-center gap-2 text-left hover:bg-slate-50/70">
        <BarChart3 className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-bold text-slate-900">What our numbers say</span>
        {ins.enough ? (
          <span className="text-xs text-slate-500">{bestHour ? `Calls connect best around ${fmtHour(bestHour.hour)}` : ''}{bestHour && bestDay ? ', ' : ''}{bestDay ? `on ${['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'][bestDay.day]}` : ''}{!bestHour && !bestDay ? `${ins.touches} touches so far` : ''}</span>
        ) : (
          <span className="text-xs text-slate-500">Collecting. {ins.touches} of {MIN_TOUCHES} touches logged; the tables open after that.</span>
        )}
        {!expanded && <span className="ml-auto text-[11px] text-slate-400">{open ? 'hide' : 'show'}</span>}
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-5 border-t border-slate-100 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <h4 className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Response by touch</h4>
            <p className="text-xs text-slate-500 mb-2">Of the touches made at each step, how many got a human response, and how many moved the store to a demo.</p>
            <table className="w-full text-sm">
              <tbody>
                {ins.byStep.map((r) => (
                  <tr key={r.key} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-700 whitespace-nowrap"><span className="text-slate-400 mr-1">{r.step}</span>{r.label}</td>
                    <td className="py-1.5 pr-2 text-right text-slate-500 whitespace-nowrap">{r.touches}</td>
                    <td className="py-1.5 w-1/3"><Bar pct={r.positivePct} /></td>
                    <td className="py-1.5 pl-2 text-right font-semibold text-slate-900 whitespace-nowrap">{r.positivePct == null ? 'none yet' : `${r.positivePct}%`}{r.moved ? <span className="text-emerald-700 font-medium"> · {r.moved} to demo</span> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500">When calls get answered</h4>
            <p className="text-xs text-slate-500 mb-2">Share of dials where someone picked up, by hour and by weekday. Test the morning-and-late-afternoon guess against the floor.</p>
            <div className="flex items-end gap-1 h-20">
              {ins.byHour.map((h) => (
                <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full" title={`${fmtHour(h.hour)}: ${h.connects} of ${h.calls} calls`}>
                  <div className={`w-full rounded-t ${h.calls >= 5 && h.pct != null && bestHour && h.hour === bestHour.hour ? 'bg-emerald-500' : h.calls ? 'bg-blue-400' : 'bg-slate-200'}`} style={{ height: `${h.calls ? Math.max(6, h.pct || 0) : 3}%` }} />
                </div>
              ))}
            </div>
            <div className="flex gap-1 mt-1">{ins.byHour.map((h) => <div key={h.hour} className="flex-1 text-center text-[9px] text-slate-400">{h.hour % 3 === 0 ? fmtHour(h.hour).replace(' ', '') : ''}</div>)}</div>
            <div className="mt-3 grid grid-cols-7 gap-1">
              {ins.byWeekday.map((d) => (
                <div key={d.day} className="text-center">
                  <div className={`text-sm font-bold ${d.calls ? 'text-slate-900' : 'text-slate-300'}`}>{d.pct == null ? '·' : `${d.pct}%`}</div>
                  <div className="text-[10px] text-slate-400">{d.label}{d.calls ? ` · ${d.calls}` : ''}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Who moves forward</h4>
            <p className="text-xs text-slate-500 mb-2">Of the stores contacted in each group, how many reached a demo or beyond, and how many closed.</p>
            <table className="w-full text-sm">
              <tbody>
                {ins.byAttribute.map((g) => (
                  <tr key={g.key} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-700">{g.key}</td>
                    <td className="py-1.5 pr-2 text-right text-slate-500 whitespace-nowrap">{g.contacted} of {g.stores}</td>
                    <td className="py-1.5 w-1/4"><Bar pct={g.progressedPct} tone="bg-violet-500" /></td>
                    <td className="py-1.5 pl-2 text-right font-semibold text-slate-900 whitespace-nowrap">{g.progressedPct == null ? 'none yet' : `${g.progressedPct}%`}{g.won ? <span className="text-emerald-700 font-medium"> · {g.won} won</span> : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Which touch opened the door</h4>
            <p className="text-xs text-slate-500 mb-2">On the {ins.firstResponse.movedTotal} store{ins.firstResponse.movedTotal === 1 ? '' : 's'} that reached a demo, the first touch that got a response.</p>
            <table className="w-full text-sm">
              <tbody>
                {ins.firstResponse.rows.map((r) => (
                  <tr key={r.step} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 text-slate-700 whitespace-nowrap"><span className="text-slate-400 mr-1">{r.step}</span>{r.label}</td>
                    <td className="py-1.5 w-1/2"><Bar pct={ins.firstResponse.total ? (r.stores / ins.firstResponse.total) * 100 : 0} tone="bg-emerald-500" /></td>
                    <td className="py-1.5 pl-2 text-right font-semibold text-slate-900">{r.stores}</td>
                  </tr>
                ))}
                {ins.firstResponse.later > 0 && <tr className="border-t border-slate-100"><td className="py-1.5 text-slate-500" colSpan={2}>After the sequence</td><td className="py-1.5 text-right font-semibold text-slate-900">{ins.firstResponse.later}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const OBJ_KEY = 'exitium.sales.objections';
const readObj = () => { try { return JSON.parse(window.localStorage.getItem(OBJ_KEY) || 'null'); } catch { return null; } };

function ObjectionSheet({ client, storeId, prospects, touches, notify }) {
  const [sheet, setSheet] = useState(readObj);
  const [busy, setBusy] = useState(false);
  const lostReasons = useMemo(() => prospects.map((p) => p.lost_reason).filter(Boolean), [prospects]);
  const notes = useMemo(() => touches.filter((t) => t.note && t.note.trim().length >= 12 && t.kind !== 'stage' && t.kind !== 'assign' && t.outcome !== 'brief').map((t) => t.note.trim()), [touches]);
  const build = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await assist(client, storeId, 'objections', { lost_reasons: lostReasons.slice(-300), notes: notes.slice(-300) });
      const next = { objections: res.objections || [], at: new Date().toISOString(), basedOn: res.based_on };
      setSheet(next);
      try { window.localStorage.setItem(OBJ_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    } catch (e) {
      console.error('sales: objections failed', e);
      notify(e.message || 'Could not build the sheet', 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-2">
        <Lightbulb className="w-4 h-4 text-amber-500" />
        <h3 className="text-base font-bold text-slate-900">When they push back</h3>
        <button type="button" onClick={build} disabled={busy} className={`${btnSmall} ml-auto`}>{busy ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Reading our notes</> : <><Sparkles className="w-3.5 h-3.5" /> {sheet ? 'Rebuild' : 'Build'} from our notes</>}</button>
      </div>
      <p className="text-xs text-slate-500 mt-1">Built from {lostReasons.length} lost reason{lostReasons.length === 1 ? '' : 's'} and {notes.length} note{notes.length === 1 ? '' : 's'} the team has logged. Rebuild every week or two.</p>
      {sheet?.objections?.length ? (
        <ul className="mt-3 divide-y divide-slate-100">
          {sheet.objections.map((o, i) => (
            <li key={i} className="py-2.5">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-semibold text-slate-900">{o.objection}</span>
                {o.heard && <span className="text-xs text-slate-500 italic">"{o.heard}"</span>}
              </div>
              {o.means && <p className="text-xs text-slate-500 mt-0.5">{o.means}</p>}
              <p className="text-sm text-slate-800 mt-1"><span className="text-[11px] font-bold uppercase tracking-wider text-blue-700 mr-1.5">Say</span>{o.say}</p>
              {o.proof && o.proof !== 'none yet' && <p className="text-xs text-emerald-800 mt-0.5"><span className="font-semibold">Proof:</span> {o.proof}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-slate-500">Nothing built yet. Press the button and the sheet writes itself from what owners have actually said to us.</p>
      )}
      {sheet?.at && <p className="mt-2 text-[11px] text-slate-400">Last built {new Date(sheet.at).toLocaleDateString()}. Kept on this device.</p>}
    </div>
  );
}

/* ---------------- the plan ---------------- */

const KIND_ICON = { call: Phone, email: Zap, dm: AtSign, visit: Footprints, text: Phone };
const stepPoints = (s) => BASE_POINTS[s.kind] || 0;

function PlanSection({ client, storeId, prospects, touches, notify, stacked = false }) {
  const [open, setOpen] = useState(0);
  const s = CADENCE[open];
  return (
    <div className={`grid grid-cols-1 gap-4 ${stacked ? '' : 'lg:grid-cols-3'}`}>
      <div className={`space-y-4 ${stacked ? '' : 'lg:col-span-2'}`}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-base font-bold text-slate-900">The seven touches</h3>
            <span className="text-xs text-slate-500">Twelve days. Each step is due from the day the one before it was actually done.</span>
          </div>
          <div className="mt-4 grid grid-cols-4 sm:grid-cols-7 gap-1.5 sm:gap-2">
            {CADENCE.map((c, i) => {
              const Icon = KIND_ICON[c.kind] || Phone;
              const on = i === open;
              return (
                <button key={c.key} type="button" onClick={() => setOpen(i)} className={`rounded-xl border p-2 sm:p-2.5 text-center transition-all ${on ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/15' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                  <div className={`mx-auto w-8 h-8 rounded-lg flex items-center justify-center ${on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}><Icon className="w-4 h-4" /></div>
                  <div className="mt-1.5 text-xs font-bold text-slate-900 truncate">{c.short}</div>
                  <div className="text-[10px] text-slate-400">Day {c.day} · +{stepPoints(c)}</div>
                </button>
              );
            })}
          </div>
          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Step {open + 1}</span>
              <span className="font-bold text-slate-900">{s.label}</span>
            </div>
            <p className="text-sm text-slate-600 mt-1">{s.goal}</p>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-slate-800 bg-white border border-slate-200 rounded-lg px-3 py-2.5">{s.say}</pre>
            {s.voicemail && <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-slate-800 bg-white border border-slate-200 rounded-lg px-3 py-2.5"><span className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">If voicemail</span>{s.voicemail}</pre>}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.outcomes.map((o) => (
                <span key={o.key} className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${o.next === 'demo' || o.next === 'pilot' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : o.next === 'lost' ? 'bg-white border-red-200 text-red-700' : 'bg-white border-slate-200 text-slate-600'}`}>{o.label}{STAGE_BONUS[o.next] ? ` +${STAGE_BONUS[o.next]}` : OUTCOME_POINTS[o.key] ? ` +${OUTCOME_POINTS[o.key]}` : ''}</span>
              ))}
            </div>
          </div>
        </div>

        <ObjectionSheet client={client} storeId={storeId} prospects={prospects} touches={touches} notify={notify} />

        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-base font-bold text-slate-900">After the sequence</h3>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`rounded-xl border p-3 sm:col-span-2 ${TONE.violet}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wider">Demo</span>
                <span className="text-[11px] font-semibold opacity-70">three steps, timed off the booked demo</span>
              </div>
              <ol className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {DEMO_STEPS.map((d, i) => (
                  <li key={d.key} className="rounded-lg bg-white/70 border border-violet-100 p-2.5">
                    <div className="font-semibold text-slate-900 text-sm"><span className="text-violet-600 mr-1">{i + 1}</span>{d.label}</div>
                    <p className="text-xs text-slate-700 mt-0.5">{d.goal}</p>
                  </li>
                ))}
              </ol>
            </div>
            {['pilot', 'customer', 'parked'].map((k) => {
              const a = STAGE_ACTIONS[k];
              const st = stageOf(k);
              return (
                <div key={k} className={`rounded-xl border p-3 ${TONE[st.tone]}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider">{st.label}</span>
                    <span className="text-[11px] font-semibold opacity-70">due in {a.dueDays} days</span>
                  </div>
                  <div className="font-semibold text-slate-900 mt-1">{a.label}</div>
                  <p className="text-sm text-slate-700 mt-0.5">{a.goal}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-base font-bold text-slate-900">How the points work</h3>
          <ul className="mt-3 text-sm text-slate-700 divide-y divide-slate-100">
            {[
              ['Call (any outcome)', `+${BASE_POINTS.call}`],
              ['Actually spoke to them', `+${OUTCOME_POINTS.spoke} more`],
              ['Email, message or text sent', `+${BASE_POINTS.email}`],
              ['Walk-in', `+${BASE_POINTS.visit}`],
              ['Showed or booked a demo', `+${BASE_POINTS.visit + OUTCOME_POINTS.showed + STAGE_BONUS.demo}`],
              ['Store reaches Demo', `+${STAGE_BONUS.demo}`],
              ['Pilot signed', `+${STAGE_BONUS.pilot}`],
              ['Paying customer', `+${STAGE_BONUS.customer}`],
              ['A referral', `+${OUTCOME_POINTS.referrals}`],
            ].map(([k, v]) => (
              <li key={k} className="flex items-center justify-between py-1.5"><span>{k}</span><span className="font-bold text-slate-900">{v}</span></li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">Daily goal {DAILY_GOAL}. The streak needs {STREAK_MIN} a day, Monday to Friday. Weekends are free. Stage bonuses pay once per store.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-base font-bold text-slate-900">Rules</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-4">
            <li>Never cold text. A text only goes to a number the owner handed you in person.</li>
            <li>The subject line and the first five seconds name East Bay Furniture.</li>
            <li>Ask for ten minutes on their floor, never for a meeting.</li>
            <li>Ask for the 30-day pilot, not for another demo.</li>
            <li>Every pilot and every customer gets asked for two store names. Add them as New.</li>
            <li>A no is a no for 60 days. Park it, then one call.</li>
          </ul>
        </div>
        <details className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 group">
          <summary className="text-base font-bold text-slate-900 cursor-pointer select-none flex items-center justify-between">Who does what, and the numbers <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" /></summary>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li><span className="font-semibold text-slate-900">The rep who owns the store does every step.</span> One voice from the first call to the pilot. An unclaimed store becomes yours the moment you log on it.</li>
            <li><span className="font-semibold text-slate-900">Start here every morning.</span> Calls first while the queue is long, then walk-ins mid-morning, email and messages in between.</li>
            <li><span className="font-semibold text-slate-900">Log the moment it happens.</span> Press the number key, the row moves itself, the next store comes up.</li>
            <li><span className="font-semibold text-slate-900">Every store is hot, warm or cold.</span> The outcome sets it, you can change it with one tap. Hot stores never sit two days without a touch.</li>
            <li><span className="font-semibold text-slate-900">Promise a call-back, keep it.</span> "Call back at a set time" brings the store back at that minute. On time is +5.</li>
            <li><span className="font-semibold text-slate-900">Nobody is lost on one no.</span> A no parks the store for 90 days and it comes back. Only "never contact us" is forever.</li>
          </ul>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700 border-t border-slate-100 pt-3">
            <li>Stores worked per rep per week: aim for 25 or more.</li>
            <li>Video replies: 5 to 15 of every 100 sent is normal.</li>
            <li>Visits that turn into a demo on the floor: the number that matters most.</li>
            <li>Demos that turn into pilots: aim for one in three.</li>
          </ul>
        </details>
      </div>
    </div>
  );
}
